import type { FactorLibrarySet } from "../api/factors";
import { quantity } from "../lib/format";
import styles from "./FactorLibraryOverview.module.css";

type Props = { status: "pending" } | { status: "failed" } | {
  status: "ready";
  sets: readonly Pick<FactorLibrarySet, "key" | "publisher" | "locked" | "factors">[];
};

export function FactorLibraryOverview(props: Props) {
  const sets = props.status === "ready" ? [...new Map(props.sets.map((set) => [set.key, set])).values()] : [];
  const metrics = [
    { key: "sets", label: "Factor sets", value: sets.length, note: "Distinct returned set keys" },
    { key: "rows", label: "Factor rows", value: sets.reduce((total, set) => total + set.factors.length, 0), note: "Rows across those returned sets" },
    { key: "publishers", label: "Publishers", value: new Set(sets.map((set) => set.publisher)).size, note: "Distinct publisher labels returned" },
    { key: "locked", label: "Locked sets", value: sets.filter((set) => set.locked).length, note: "Returned sets marked locked" },
  ];
  return <section className={styles.overview} aria-label="Factor library summary" data-testid="factor-library-summary" aria-busy={props.status === "pending"}>
    <dl className={styles.metrics}>{metrics.map((metric) => <div key={metric.key} className={styles.metric} data-testid={`factor-library-stat-${metric.key}`}>
      <dt>{metric.label}</dt>
      <dd>{props.status === "pending" ? <span className={styles.skeleton} aria-hidden="true" /> : props.status === "failed" ? "—" : quantity(String(metric.value), 0)}</dd>
      <span role="definition" className={styles.note}>{metric.note}</span>
    </div>)}</dl>
    {props.status === "pending" ? <p className={styles.note} role="status">Reading the factor library…</p> : props.status === "failed" ? (
      <p className={styles.note}>The factor library could not be read. Set, row, publisher and lock counts are unknown.</p>
    ) : <p className={styles.note}>Counts describe the returned library records, not use by figures or the set selected for a reporting period. A factor row is not an emissions figure. Spend-based multiplier sets are held separately and are not counted here.</p>}
  </section>;
}
